import { useCallback, useState } from 'react';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Stack } from '@rossum/ui/material';
import { QUEUE_MANAGER_QUERY } from '../../../redux/modules/localStorage/actions';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import useDebounce from '../../../utils/hooks/useDebounce';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { useSortWorkspacesWithQueues } from '../../queue-sidebar/sidebar/hooks/useSortWorkspacesWithQueues';
import { useWorkspaceSorting } from '../../queue-sidebar/sidebar/hooks/useWorkspaceSorting';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { useAllEngines } from '../hooks/useAllEngines';
import {
  hasAnyActiveFilter,
  useFilteredWorkspaces,
} from '../hooks/useFilteredWorkspaces';
import {
  queueManagerQueryParser,
  useQueueManagerFilterSettings,
} from '../hooks/useQueueManagerFilterSettings';
import { WorkspaceSectionSkeleton } from './queue-tile/Skeletons';
import { QueueManagerHeader } from './QueueManagerHeader';
import { QueuesList } from './QueuesList';
import { Toolbar } from './Toolbar';

export const QueueManager = () => {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 250);

  // Filters are stored in URL query params and localStorage for persistence
  const [filters, setFilters] = useStoredQuery({
    parser: queueManagerQueryParser,
    localStorageKey: QUEUE_MANAGER_QUERY,
  });

  const {
    enginesFilterOptions,
    isLoading: isEnginesLoading,
    transformEngineFiltersToQuery,
    getEngineChipData,
  } = useAllEngines();

  const {
    allWorkspacesWithQueues,
    workspaces,
    isWorkspacesFetching,
    isQueuesFetching,
  } = useWorkspacesWithQueues({
    enableQueries: true,
    refetchInterval: undefined,
    queueFilters: {
      workspace: filters.workspaces,
      hooks: filters.extensions,
      automationLevel: filters.automationLevel,
      enginesFilterMode: 'OR',
      ...transformEngineFiltersToQuery(filters.engines),
    },
  });

  const { sorting, setSorting } = useWorkspaceSorting();

  const filterSettings = useQueueManagerFilterSettings({
    workspaces,
    isWorkspacesLoading: isWorkspacesFetching,
    enginesFilterOptions,
    isEnginesLoading,
    setFilters,
  });

  const { sortedWorkspaces } = useSortWorkspacesWithQueues({
    workspaces: allWorkspacesWithQueues ?? [],
    sorting,
  });

  const filteredWorkspaces = useFilteredWorkspaces({
    workspaces: sortedWorkspaces,
    searchValue: debouncedSearchValue,
    filters,
  });

  const showSkeleton = !allWorkspacesWithQueues && isWorkspacesFetching;

  // Whether the (possibly empty) result set is the product of an active
  // search/filter, so the list can distinguish "no matches" from "nothing yet".
  const isFiltered =
    hasAnyActiveFilter(filters) || debouncedSearchValue.trim() !== '';

  const handleClearFilters = useCallback(() => {
    setSearchValue('');
    setFilters({
      workspaces: undefined,
      engines: undefined,
      extensions: undefined,
      automationLevel: undefined,
    });
  }, [setFilters]);

  return (
    <>
      <HelmetComponent translationKey="features.routes.pageTitles.queueManager" />
      <PageLayout renderHeader={props => <QueueManagerHeader {...props} />}>
        <Stack p={4} flex={1} spacing={2}>
          <Toolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            sorting={sorting}
            onSortingChange={setSorting}
            filters={filters}
            setFilters={setFilters}
            filterSettings={filterSettings}
            isFiltersLoading={isWorkspacesFetching}
          />
          <Stack position="relative" flexGrow={1} height={1}>
            {showSkeleton ? (
              <WorkspaceSectionSkeleton />
            ) : (
              <QueuesList
                allWorkspacesWithQueues={filteredWorkspaces}
                workspaces={workspaces}
                isQueuesFetching={isQueuesFetching}
                getEngineChipData={getEngineChipData}
                isFiltered={isFiltered}
                onClearFilters={handleClearFilters}
              />
            )}
          </Stack>
        </Stack>
      </PageLayout>
    </>
  );
};
